import React, { FC, useEffect, useMemo, useRef } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { CircularProgress, Paper, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import gsap from 'gsap';
import clsx from 'clsx';
import { useQuery } from '@apollo/client';
import {
  TimePeriodPicker,
  PeriodParams,
  parsePeriodParams,
  periodParamsToSearch,
  isCurrentOrFuture,
  periodParamsToStartDate,
} from './ExecutiveAnalytics';
import Divider from '@material-ui/core/Divider';

import {
  GROUP_PERFORMANCE_SUMMARY_QUERY,
  EXECUTIVE_KPI_EARLIEST_DATE_QUERY,
  GroupPerformanceSummaryData,
  GroupPerformanceSummaryVars,
  ExecutiveKpiEarliestDateData,
  toAnalyticsPeriod,
  GroupPerformanceHospital,
} from '@/graph/group-performance';

const Stat = ({
  title,
  value,
  indication,
  onClick,
}: {
  title: string;
  value: number;
  indication?: 'good' | 'warning' | 'severe';
  onClick?: () => void;
}) => {
  const classes = useStyles();

  return (
    <Box flex={1} textAlign="center" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <Typography
        variant="h1"
        className={clsx(classes.statValue, {
          [classes.statGood]: indication === 'good',
          [classes.statWarning]: indication === 'warning',
          [classes.statSevere]: indication === 'severe',
        })}
      >
        {value}
      </Typography>
    </Box>
  );
};

const StatBox = ({
  title,
  average,
  best,
  worst,
  bestIndication,
  worstIndication,
  onBestClick,
  onWorstClick,
  unavailable,
  hideRankings,
}: {
  title: string;
  average?: number;
  best?: number;
  worst?: number;
  bestIndication?: 'good' | 'warning' | 'severe';
  worstIndication?: 'good' | 'warning' | 'severe';
  onBestClick?: () => void;
  onWorstClick?: () => void;
  unavailable?: boolean;
  hideRankings?: boolean;
}) => {
  const classes = useStyles();

  if (unavailable) {
    return (
      <Box className={classes.statBox}>
        <Typography variant="h5" className={classes.statTitle}>
          {title} average
        </Typography>
        <Typography variant="h2" style={{ opacity: 0.25 }}>
          —
        </Typography>
      </Box>
    );
  }

  const statContent = (
    <Box textAlign="center" style={{ cursor: 'default' }}>
      <Stat title="Average" value={average!} />
    </Box>
  );

  return (
    <Box className={classes.statBox}>
      <Typography variant="h5" className={classes.statTitle}>
        {title}
        {hideRankings ? '' : ' average'}
      </Typography>
      {hideRankings ? (
        statContent
      ) : (
        <Tooltip
          interactive
          placement="bottom"
          classes={{ tooltip: classes.statTooltip }}
          title={
            <Box display="flex" flexDirection="column">
              <Box className={classes.tooltipStat} onClick={onBestClick}>
                <Typography variant="h5" style={{ flex: 1 }}>
                  Best
                </Typography>
                <Stat title="Best" value={best!} indication={bestIndication} />
              </Box>
              <Divider />
              <Box className={classes.tooltipStat} onClick={onWorstClick}>
                <Typography variant="h5" style={{ flex: 1 }}>
                  Worst
                </Typography>
                <Stat title="Worst" value={worst!} indication={worstIndication} />
              </Box>
            </Box>
          }
        >
          {statContent}
        </Tooltip>
      )}
    </Box>
  );
};

const SmallStat = ({
  title,
  value,
  indication,
  trend,
  trendValue,
  rank,
  secondaryValue,
}: {
  title: string;
  value: string;
  indication?: 'good' | 'warning' | 'severe';
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  rank?: 'best' | 'worst' | 'most' | 'least';
  secondaryValue?: string;
  delay?: number;
}) => {
  const classes = useStyles();

  // Extract numeric part and unit suffix (handles negatives like "-925 min")
  const numericValue = parseFloat(value);
  const unitSuffix = isNaN(numericValue) ? '' : value.replace(/^-?[\d.]+/, '').trim();

  return (
    <Box className={classes.kpiTile}>
      <Typography className={classes.kpiLabel}>{title}</Typography>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Typography
          variant="h4"
          className={clsx({
            [classes.statGood]: indication === 'good',
            [classes.statWarning]: indication === 'warning',
            [classes.statSevere]: indication === 'severe',
          })}
          style={{
            fontWeight: 'bold',
            lineHeight: 1.1,
            fontSize: '1.75rem',
          }}
        >
          {unitSuffix ? (isNaN(numericValue) ? value : numericValue) : value}
          {unitSuffix && (
            <span
              style={{
                fontSize: '0.55em',
                fontWeight: 400,
                marginLeft: 3,
                opacity: 0.7,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {unitSuffix}
            </span>
          )}
        </Typography>

        {rank && (
          <Tooltip
            title={
              rank === 'best'
                ? 'Best value among all centers in the group'
                : rank === 'worst'
                  ? 'Worst value among all centers in the group'
                  : rank === 'most'
                    ? 'Most cases among all centers in the group — volume depends on center size'
                    : 'Least cases among all centers in the group — volume depends on center size'
            }
          >
            <Typography
              className={
                rank === 'best' ? classes.rankBest : rank === 'worst' ? classes.rankWorst : classes.rankNeutral
              }
            >
              {rank === 'best' ? 'Best' : rank === 'worst' ? 'Worst' : rank === 'most' ? 'Most' : 'Least'}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {trend && trendValue && (
        <Typography
          className={clsx(classes.trendText, {
            [classes.statGood]: indication === 'good',
            [classes.statWarning]: indication === 'warning',
            [classes.statSevere]: indication === 'severe',
          })}
        >
          {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trendValue}
        </Typography>
      )}
      {secondaryValue && (
        <Typography style={{ color: 'rgba(255,255,255,0.58)', fontSize: '0.72rem', marginTop: 6 }}>
          {secondaryValue}
        </Typography>
      )}
    </Box>
  );
};

const Panel = React.forwardRef<
  HTMLDivElement,
  {
    organizationId: string;
    title: string;
    fcotsAverageDelayMinutes: number | null;
    fcotsPercentage: number | null;
    lpodAverageDelayMinutes: number | null;
    lpodPercentage: number | null;
    turnover: number | null;
    utilization: number | null;
    cases: number;
    rtor: number | null;
    isDayView: boolean;
    rankings?: {
      fcots?: 'best' | 'worst';
      lpod?: 'best' | 'worst';
      turnover?: 'best' | 'worst';
      utilization?: 'best' | 'worst';
      cases?: 'best' | 'worst' | 'most' | 'least';
      rtor?: 'best' | 'worst';
    };
    panelIndex?: number;
  }
>(
  (
    {
      organizationId,
      title,
      fcotsAverageDelayMinutes,
      fcotsPercentage,
      lpodAverageDelayMinutes,
      lpodPercentage,
      turnover,
      utilization,
      cases,
      rtor,
      isDayView,
      rankings,
      panelIndex = 0,
    },
    ref
  ) => {
    const classes = useStyles();
    const borderColor = 'transparent';

    const analyticsUrl = `/su/${organizationId}`;

    return (
      <Link to={analyticsUrl} style={{ textDecoration: 'none', display: 'contents' }}>
        <Paper ref={ref} className={classes.panel} style={{ borderColor: borderColor, cursor: 'pointer' }}>
          <Typography className={classes.panelTitle}>{title}</Typography>

          <Box className={classes.kpiGrid}>
            <SmallStat title="Cases" value={`${cases}`} rank={rankings?.cases} delay={0.2 + panelIndex * 0.05} />
            <SmallStat
              title="Turnover"
              value={turnover !== null ? `${turnover} min` : '—'}
              rank={rankings?.turnover}
              delay={0.2 + panelIndex * 0.05}
            />
            <SmallStat
              title="Utilization"
              value={utilization !== null ? `${utilization}%` : '—'}
              rank={rankings?.utilization}
              delay={0.2 + panelIndex * 0.05}
            />
            <SmallStat
              title="FCOTS"
              value={fcotsPercentage !== null ? `${fcotsPercentage.toFixed(1)}%` : '—'}
              secondaryValue={
                fcotsAverageDelayMinutes !== null ? `${fcotsAverageDelayMinutes} min avg delay` : undefined
              }
              rank={rankings?.fcots}
              delay={0.2 + panelIndex * 0.05}
            />
            <SmallStat
              title="LPOD"
              value={lpodAverageDelayMinutes !== null ? `${lpodAverageDelayMinutes} min` : '—'}
              indication={
                lpodAverageDelayMinutes !== null
                  ? isDayView && lpodAverageDelayMinutes <= 0
                    ? 'good'
                    : 'severe'
                  : undefined
              }
              secondaryValue={
                lpodPercentage !== null
                  ? isDayView
                    ? `On-Time ${(100 - lpodPercentage).toFixed(1)}% • Late ${lpodPercentage.toFixed(1)}%`
                    : `Late finishes ${lpodPercentage.toFixed(1)}%`
                  : undefined
              }
              rank={rankings?.lpod}
              delay={0.2 + panelIndex * 0.05}
            />
            <SmallStat
              title="RTOR"
              value={rtor !== null ? `${rtor} min` : '—'}
              rank={rankings?.rtor}
              delay={0.2 + panelIndex * 0.05}
            />
          </Box>
        </Paper>
      </Link>
    );
  }
);

Panel.displayName = 'Panel';

const GroupAdminAnalytics: FC<RouteComponentProps> = ({ history, location }) => {
  const classes = useStyles();

  const panelsRef = useRef<(HTMLDivElement | null)[]>([]);
  const now = useMemo(() => new Date(), []);

  // Read period from URL query params
  const periodParams = useMemo(() => parsePeriodParams(location.search, now), [location.search, now]);
  const selectedPeriod = toAnalyticsPeriod(periodParams.t);
  const startDate = isCurrentOrFuture(periodParams, now) ? undefined : periodParamsToStartDate(periodParams);

  // Fetch earliest available date (Apollo caches after first load)
  const { data: earliestDateData } = useQuery<ExecutiveKpiEarliestDateData>(EXECUTIVE_KPI_EARLIEST_DATE_QUERY);
  const earliestDate = earliestDateData?.executiveKpiEarliestDate ?? undefined;

  // Fetch real data from the API.
  const {
    data,
    loading,
    error,
    variables: fetchedVariables,
  } = useQuery<GroupPerformanceSummaryData, GroupPerformanceSummaryVars>(GROUP_PERFORMANCE_SUMMARY_QUERY, {
    variables: { period: selectedPeriod, startDate },
    fetchPolicy: 'cache-and-network',
  });

  // Only use data whose period matches the currently selected period.
  // This prevents stale cached data from a different period being shown
  // while a new fetch is in flight.
  const dataMatchesPeriod = fetchedVariables?.period === selectedPeriod;
  const hospitals: GroupPerformanceHospital[] = useMemo(
    () => (dataMatchesPeriod && !error ? (data?.groupPerformanceSummary?.hospitals ?? []) : []),
    [data, dataMatchesPeriod, error]
  );

  useEffect(() => {
    // Animate panels with stagger when hospitals data changes
    if (hospitals.length > 0) {
      gsap.from(panelsRef.current.filter(Boolean), {
        opacity: 0,
        y: 20,
        duration: 0.6,
        stagger: 0.05,
        ease: 'power2.out',
        delay: 0.2,
      });
    }
  }, [hospitals]);

  // Handle period change — write to URL
  const handlePeriodChange = (params: PeriodParams) => {
    const newSearch = periodParamsToSearch(params, location.search);
    history.replace({ search: newSearch });
  };

  const calculateStats = (metric: 'fcots' | 'lpod' | 'turnover' | 'utilization' | 'cases' | 'rtor') => {
    const withValues = hospitals
      .map((item, i) => ({
        value:
          metric === 'fcots'
            ? item.fcots.percentage
            : metric === 'lpod'
              ? item.lpod.averageDelayMinutes
              : metric === 'rtor'
                ? item.rtor
                : item[metric],
        index: i,
      }))
      .filter((h): h is { value: number; index: number } => h.value !== null);
    if (withValues.length === 0) {
      return {
        average: 0,
        best: 0,
        worst: 0,
        bestIndex: -1,
        worstIndex: -1,
        unavailable: hospitals.length > 0 && metric !== 'cases',
      };
    }
    const values = withValues.map(h => h.value);
    const average = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    const lowerIsBetter = metric === 'lpod' || metric === 'turnover' || metric === 'rtor';
    const best = lowerIsBetter ? Math.min(...values) : Math.max(...values);
    const worst = lowerIsBetter ? Math.max(...values) : Math.min(...values);
    const bestEntry = withValues.find(h => h.value === best)!;
    const worstEntry = withValues.find(h => h.value === worst)!;
    return { average, best, worst, bestIndex: bestEntry.index, worstIndex: worstEntry.index, unavailable: false };
  };

  const isDayView = periodParams.t === 'd';
  const fcotsStats = useMemo(() => calculateStats('fcots'), [hospitals]);
  const lpodStats = useMemo(() => calculateStats('lpod'), [hospitals]);
  const turnoverStats = useMemo(() => calculateStats('turnover'), [hospitals]);
  const utilizationStats = useMemo(() => calculateStats('utilization'), [hospitals]);
  const casesStats = useMemo(() => calculateStats('cases'), [hospitals]);
  const rtorStats = useMemo(() => calculateStats('rtor'), [hospitals]);

  const getIndicationColor = (indication?: 'good' | 'warning' | 'severe'): string => {
    switch (indication) {
      case 'good':
        return '#4ade80';
      case 'warning':
        return '#fb923c';
      case 'severe':
        return '#f87171';
      default:
        return '#22d3ee';
    }
  };

  const flashPanel = (index: number, color: string) => {
    const panel = panelsRef.current[index];
    if (panel) {
      panel.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      gsap.fromTo(
        panel,
        { outline: '0px solid transparent' },
        {
          outline: `5px solid ${color}`,
          duration: 0.3,
          repeat: 5,
          yoyo: true,
          ease: 'power2.inOut',
          onComplete: () => {
            gsap.to(panel, { outline: '0px solid transparent', duration: 0.2 });
          },
        }
      );
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <Paper>
        <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  // Handle empty state (no surgery centers)
  if (hospitals.length === 0) {
    return (
      <Paper>
        <Box p={4}>
          <Box
            mb={4}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: 16 }}
          >
            <Typography variant="h3">Group Performance Summary</Typography>
            <Box className={classes.pickerWrapper}>
              <TimePeriodPicker periodParams={periodParams} earliestDate={earliestDate} onChange={handlePeriodChange} />
            </Box>
          </Box>
          <Alert severity="info">No surgery-center analytics available for the selected period.</Alert>
        </Box>
      </Paper>
    );
  }

  return (
    <React.Fragment>
      <Paper>
        <Box p={4}>
          {error && (
            <Box mb={2}>
              <Alert severity="warning">Live group-performance data is unavailable.</Alert>
            </Box>
          )}
          <Box
            mb={4}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: 16 }}
          >
            <Typography variant="h3">Group Performance Summary</Typography>
            <Box className={classes.pickerWrapper}>
              <TimePeriodPicker periodParams={periodParams} earliestDate={earliestDate} onChange={handlePeriodChange} />
            </Box>
          </Box>

          <Box className={classes.mainStats}>
            <StatBox
              title="Cases"
              average={casesStats.average}
              best={casesStats.best}
              worst={casesStats.worst}
              onBestClick={() => flashPanel(casesStats.bestIndex, getIndicationColor())}
              onWorstClick={() => flashPanel(casesStats.worstIndex, getIndicationColor())}
              hideRankings={hospitals.length <= 1}
            />
            <StatBox
              title="Turnover (min)"
              unavailable={turnoverStats.unavailable}
              average={turnoverStats.average}
              best={turnoverStats.best}
              worst={turnoverStats.worst}
              onBestClick={() => flashPanel(turnoverStats.bestIndex, getIndicationColor())}
              onWorstClick={() => flashPanel(turnoverStats.worstIndex, getIndicationColor())}
              hideRankings={hospitals.length <= 1}
            />
            <StatBox
              title="Utilization (%)"
              unavailable={utilizationStats.unavailable}
              average={utilizationStats.average}
              best={utilizationStats.best}
              worst={utilizationStats.worst}
              onBestClick={() => flashPanel(utilizationStats.bestIndex, getIndicationColor())}
              onWorstClick={() => flashPanel(utilizationStats.worstIndex, getIndicationColor())}
              hideRankings={hospitals.length <= 1}
            />
            <StatBox
              title="FCOTS (%)"
              unavailable={fcotsStats.unavailable}
              average={fcotsStats.average}
              best={fcotsStats.best}
              worst={fcotsStats.worst}
              onBestClick={() => flashPanel(fcotsStats.bestIndex, getIndicationColor())}
              onWorstClick={() => flashPanel(fcotsStats.worstIndex, getIndicationColor())}
              hideRankings={hospitals.length <= 1}
            />
            <StatBox
              title="LPOD (min)"
              unavailable={lpodStats.unavailable}
              average={lpodStats.average}
              best={lpodStats.best}
              worst={lpodStats.worst}
              bestIndication={lpodStats.unavailable ? undefined : isDayView && lpodStats.best <= 0 ? 'good' : 'severe'}
              worstIndication={lpodStats.unavailable ? undefined : 'severe'}
              onBestClick={() =>
                flashPanel(
                  lpodStats.bestIndex,
                  getIndicationColor(isDayView && lpodStats.best <= 0 ? 'good' : 'severe')
                )
              }
              onWorstClick={() => flashPanel(lpodStats.worstIndex, getIndicationColor('severe'))}
              hideRankings={hospitals.length <= 1}
            />
            <StatBox
              title="RTOR (min)"
              unavailable={rtorStats.unavailable}
              average={rtorStats.average}
              best={rtorStats.best}
              worst={rtorStats.worst}
              onBestClick={() => flashPanel(rtorStats.bestIndex, getIndicationColor())}
              onWorstClick={() => flashPanel(rtorStats.worstIndex, getIndicationColor())}
              hideRankings={hospitals.length <= 1}
            />
          </Box>
        </Box>
      </Paper>

      <Box display="flex" className={classes.panels}>
        {hospitals.map((item, index) => {
          const rankings: Record<string, 'best' | 'worst' | 'most' | 'least'> = {};
          if (hospitals.length > 1) {
            if (index === fcotsStats.bestIndex) rankings.fcots = 'best';
            else if (index === fcotsStats.worstIndex) rankings.fcots = 'worst';
            if (index === lpodStats.bestIndex) rankings.lpod = 'best';
            else if (index === lpodStats.worstIndex) rankings.lpod = 'worst';
            if (index === turnoverStats.bestIndex) rankings.turnover = 'best';
            else if (index === turnoverStats.worstIndex) rankings.turnover = 'worst';
            if (index === utilizationStats.bestIndex) rankings.utilization = 'best';
            else if (index === utilizationStats.worstIndex) rankings.utilization = 'worst';
            if (index === casesStats.bestIndex) rankings.cases = 'most';
            else if (index === casesStats.worstIndex) rankings.cases = 'least';
            if (index === rtorStats.bestIndex) rankings.rtor = 'best';
            else if (index === rtorStats.worstIndex) rankings.rtor = 'worst';
          }

          return (
            <Panel
              key={item.organizationId || item.hospitalName}
              ref={el => (panelsRef.current[index] = el)}
              title={item.hospitalName}
              organizationId={item.organizationId}
              fcotsAverageDelayMinutes={
                item.fcots.averageDelayMinutes !== null ? Math.round(item.fcots.averageDelayMinutes) : null
              }
              fcotsPercentage={item.fcots.percentage !== null ? Math.round(item.fcots.percentage * 10) / 10 : null}
              lpodAverageDelayMinutes={
                item.lpod.averageDelayMinutes !== null ? Math.round(item.lpod.averageDelayMinutes) : null
              }
              lpodPercentage={item.lpod.percentage !== null ? Math.round(item.lpod.percentage * 10) / 10 : null}
              turnover={item.turnover !== null ? Math.round(item.turnover) : null}
              utilization={item.utilization !== null ? Math.round(item.utilization * 10) / 10 : null}
              cases={item.cases}
              rtor={item.rtor !== null ? Math.round(item.rtor) : null}
              isDayView={isDayView}
              rankings={rankings}
              panelIndex={index}
            />
          );
        })}
      </Box>
    </React.Fragment>
  );
};

const useStyles = makeStyles(theme => ({
  statBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderRight: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1),
    },
  },
  statTitle: {
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.75rem',
    },
  },
  statValue: {
    fontWeight: 500,
    fontSize: '3em',
    [theme.breakpoints.down('xs')]: {
      fontSize: '2em',
    },
  },
  statTooltip: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    maxWidth: 'none',
  },
  tooltipStat: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 200,
  },
  mainStats: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(3, 1fr)',
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(6, 1fr)',
    },
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1),
    },
  },
  pickerWrapper: {
    overflow: 'hidden',
  },
  panels: {
    padding: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    [theme.breakpoints.up('xl')]: {
      gridTemplateColumns: 'repeat(4, 1fr)',
    },
  },
  panel: {
    padding: theme.spacing(2.5),
    backgroundColor: 'rgba(255, 255, 255, 0.065)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderWidth: 3,
    borderColor: 'transparent',
    borderStyle: 'solid',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  panelTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    letterSpacing: '0.01em',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(0.5),
  },
  kpiGridAverage: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  kpiTile: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  kpiLabel: {
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
  },
  trendText: {
    fontSize: '0.7rem',
    fontWeight: 700,
    marginTop: theme.spacing(0.5),
    opacity: 0.85,
  },
  rankBest: {
    display: 'inline-block',
    fontSize: '0.6rem',
    fontWeight: 700,
    color: '#01dd52',
    backgroundColor: 'rgba(1, 221, 82, 0.1)',
    padding: '1px 6px',
    borderRadius: 4,
    marginTop: theme.spacing(0.25),
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: 'default',
  },
  rankWorst: {
    display: 'inline-block',
    fontSize: '0.6rem',
    fontWeight: 700,
    color: '#ff3535',
    backgroundColor: 'rgba(255, 53, 53, 0.1)',
    padding: '1px 6px',
    borderRadius: 4,
    marginTop: theme.spacing(0.25),
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: 'default',
  },
  rankNeutral: {
    display: 'inline-block',
    fontSize: '0.6rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    padding: '1px 6px',
    borderRadius: 4,
    marginTop: theme.spacing(0.25),
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: 'default',
  },
  statGood: {
    color: '#01dd52', // Bright green (Tailwind green-400)
  },
  statWarning: {
    color: '#ff8c2d', // Bright orange (Tailwind orange-400)
  },
  statSevere: {
    color: '#ff3535', // Bright red (Tailwind red-400)
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',

      '& > *': {
        marginLeft: 0,
        width: '100%',
      },

      '& > a': {
        marginRight: '0.5rem',
      },
    },
  },
}));

export default GroupAdminAnalytics;
